import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { fetchCostData, fetchCostByAccount, fetchCostTrend, fetchAccountServiceTrend, getMe, logout as logoutRequest } from "./api";
import DateFilter from "./components/DateFilter";
import CostTable from "./components/CostTable";
import AccountOrgTree from "./components/AccountOrgTree";
import TrendKpiCard from "./components/TrendKpiCard";
import AccountTrendChart from "./components/AccountTrendChart";
import AccountBarBreakdown from "./components/AccountBarBreakdown";
import AccountServicesPanel from "./components/AccountServicesPanel";
import Modal from "./components/Modal";
import LoginPage from "./components/LoginPage";
import { ChevronLeftIcon } from "./components/icons";
import { formatCurrency } from "./utils/format";
import { PRESETS, getPreviousMonthRange } from "./utils/dateRanges";
import {
  sumDaily,
  filterDailyInRange,
  bucketByMonth,
  getFYQuartersToDate,
  getCurrentQuarterStartDate,
  getQuarterDateRange,
  getTopServices,
  bucketServiceTrendByMonth,
  bucketServiceTrendByWeek,
  getAllAccounts,
  bucketAllAccountsTrendByDay,
  bucketAllAccountsTrendByWeek,
  bucketAllAccountsTrendByMonth,
  bucketAllAccountsTrendByDayInRange,
  bucketAllAccountsTrendByWeekInRange,
  bucketAllAccountsTrendByQuarterMonths
} from "./utils/trend";
import soriginLogo from "./assets/sorigin-logo.svg";

const SORIGIN_PROD_ACCOUNT_ID = "533267294395";

const isDevAccount = (a) => a.environment === "dev";
const isProdAccount = (a) => a.environment === "prod";

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [view, setView] = useState("accounts"); // 'accounts' | 'services'
  const [preset, setPreset] = useState(PRESETS.PREVIOUS_MONTH);
  const [dateRange, setDateRange] = useState(getPreviousMonthRange());
  const [dateRangeWarning, setDateRangeWarning] = useState("");

  const [accountsData, setAccountsData] = useState(null);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [trendDaily, setTrendDaily] = useState([]);
  const [trendAsOf, setTrendAsOf] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [expandedKpi, setExpandedKpi] = useState(null); // 'yearly' | 'quarterly' | 'dev' | 'prod' | null
  const [yearlyGranularity, setYearlyGranularity] = useState("monthly"); // 'daily' | 'weekly' | 'monthly'
  const [quarterlyGranularity, setQuarterlyGranularity] = useState("monthly"); // 'daily' | 'weekly' | 'monthly'

  const [selectedAccount, setSelectedAccount] = useState(null); // { id, name }

  const [totalData, setTotalData] = useState(null);
  const [solarData, setSolarData] = useState(null);
  const [windData, setWindData] = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [servicesLoading, setServicesLoading] = useState(false);

  const [accountTrendDaily, setAccountTrendDaily] = useState([]);
  const [accountTrendLoading, setAccountTrendLoading] = useState(false);
  const [accountTrendGranularity, setAccountTrendGranularity] = useState("monthly"); // 'monthly' | 'weekly'

  const loadAccounts = async (range) => {
    try {
      setAccountsLoading(true);
      const data = await fetchCostByAccount(range.startDate, range.endDate);
      setAccountsData(data);
    } catch (err) {
      console.error(err);
      alert("Error fetching account data");
    } finally {
      setAccountsLoading(false);
    }
  };

  const loadTrend = async () => {
    try {
      setTrendLoading(true);
      const data = await fetchCostTrend();
      setTrendDaily(data.daily);
      setTrendAsOf(data.asOf);
    } catch (err) {
      console.error(err);
    } finally {
      setTrendLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        setUser(me);
      } catch (err) {
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;

    loadAccounts(dateRange);
    loadTrend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = async () => {
    try {
      await logoutRequest();
    } catch (err) {
      console.error(err);
    }

    setUser(null);
    setView("accounts");
    setSelectedAccount(null);
    setAccountsData(null);
    setTrendDaily([]);
    setTrendAsOf(null);
    setTotalData(null);
    setSolarData(null);
    setWindData(null);
    setServiceData(null);
    setAccountTrendDaily([]);
    setDateRangeWarning("");
    window.history.replaceState({ view: "accounts" }, "");
  };

  // Auto-logout after 30 minutes of no user activity. A single setTimeout
  // is cleared and restarted on every activity event, rather than a
  // setInterval polling loop, so there's no wasted work while the user is
  // actively using the app.
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;
    let timeoutId;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        handleLogout();
      }, INACTIVITY_LIMIT_MS);
    };

    const activityEvents = ["mousemove", "keydown", "click"];
    activityEvents.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      activityEvents.forEach(event => window.removeEventListener(event, resetTimer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const monthlyBuckets = useMemo(() => bucketByMonth(trendDaily), [trendDaily]);
  const ytdTotal = useMemo(() => sumDaily(trendDaily), [trendDaily]);

  // Computed once from the full YTD series so an account's color stays
  // identical across the YTD chart, the Quarterly chart, and every
  // day/week/month toggle.
  const topAccounts = useMemo(() => getAllAccounts(trendDaily), [trendDaily]);

  const yearlyAccountBuckets = useMemo(() => {
    if (yearlyGranularity === "daily") return bucketAllAccountsTrendByDay(trendDaily, topAccounts.accounts);
    if (yearlyGranularity === "weekly") return bucketAllAccountsTrendByWeek(trendDaily, topAccounts.accounts);
    return bucketAllAccountsTrendByMonth(trendDaily, topAccounts.accounts);
  }, [trendDaily, yearlyGranularity, topAccounts]);

  const { quarters, year: fiscalYear } = useMemo(() => getFYQuartersToDate(), [trendAsOf]);
  const currentQuarter = quarters[quarters.length - 1];

  const currentQuarterSparkline = useMemo(() => {
    if (!currentQuarter || !trendAsOf) return [];
    const quarterStart = getCurrentQuarterStartDate(currentQuarter, fiscalYear);
    return filterDailyInRange(trendDaily, quarterStart, trendAsOf).map(d => ({
      label: dayjs(d.date).format("MMM D"),
      fullLabel: dayjs(d.date).format("MMM D, YYYY"),
      value: d.cost
    }));
  }, [trendDaily, currentQuarter, fiscalYear, trendAsOf]);

  const quarterTotals = useMemo(() => {
    return quarters.map(q => {
      const { startDate, endDate } = getQuarterDateRange(q, fiscalYear);
      return {
        ...q,
        startDate,
        endDate,
        total: sumDaily(filterDailyInRange(trendDaily, startDate, endDate))
      };
    });
  }, [quarters, fiscalYear, trendDaily]);

  const currentQuarterTotal = useMemo(
    () => sumDaily(currentQuarterSparkline.map(d => ({ cost: d.value }))),
    [currentQuarterSparkline]
  );

  const yearlySparkline = useMemo(
    () => monthlyBuckets.map(m => ({ label: m.label, value: m.value })),
    [monthlyBuckets]
  );

  const devAccounts = useMemo(() => {
    if (!accountsData) return [];
    return accountsData.breakdown
      .filter(a => isDevAccount(a) && a.cost !== null)
      .map(a => ({ name: a.name, cost: parseFloat(a.cost) }));
  }, [accountsData]);

  const prodAccounts = useMemo(() => {
    if (!accountsData) return [];
    return accountsData.breakdown
      .filter(a => isProdAccount(a) && a.cost !== null)
      .map(a => ({ name: a.name, cost: parseFloat(a.cost) }));
  }, [accountsData]);

  const devSpend = useMemo(() => devAccounts.reduce((sum, a) => sum + a.cost, 0), [devAccounts]);
  const prodSpend = useMemo(() => prodAccounts.reduce((sum, a) => sum + a.cost, 0), [prodAccounts]);

  const topServices = useMemo(() => getTopServices(accountTrendDaily), [accountTrendDaily]);

  const accountTrendBuckets = useMemo(() => {
    return accountTrendGranularity === "monthly"
      ? bucketServiceTrendByMonth(accountTrendDaily, topServices.services, topServices.hasOther)
      : bucketServiceTrendByWeek(accountTrendDaily, topServices.services, topServices.hasOther);
  }, [accountTrendDaily, accountTrendGranularity, topServices]);

  const loadAccountTrend = async (account) => {
    try {
      setAccountTrendLoading(true);
      const data = await fetchAccountServiceTrend(account.id);
      setAccountTrendDaily(data.daily);
    } catch (err) {
      console.error(err);
    } finally {
      setAccountTrendLoading(false);
    }
  };

  const loadAccountServices = async (account, range) => {
    try {
      setServicesLoading(true);

      if (account.id === SORIGIN_PROD_ACCOUNT_ID) {
        const [total, solar, wind] = await Promise.all([
          fetchCostData(range.startDate, range.endDate, null, account.id),
          fetchCostData(range.startDate, range.endDate, "Budget-Solar", account.id),
          fetchCostData(range.startDate, range.endDate, "Budget-Wind", account.id)
        ]);

        setTotalData(total);
        setSolarData(solar);
        setWindData(wind);
      } else {
        const data = await fetchCostData(range.startDate, range.endDate, null, account.id);
        setServiceData(data);
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching data");
    } finally {
      setServicesLoading(false);
    }
  };

  const handleFilterChange = (nextPreset, nextRange, shouldSearch) => {
    setPreset(nextPreset);
    setDateRange(nextRange);

    if (!shouldSearch) return;

    const today = dayjs().format("YYYY-MM-DD");

    if (nextRange.startDate > today || nextRange.endDate > today) {
      setDateRangeWarning("That date range is in the future — no cost data exists yet. Choose a range up to today.");
      return;
    }

    setDateRangeWarning("");
    loadAccounts(nextRange);

    if (view === "services" && selectedAccount) {
      loadAccountServices(selectedAccount, nextRange);
    }
  };

  const calculateAdjustedCosts = (totalData, solarData, windData) => {
    if (!totalData || !solarData || !windData) return {};

    const total = parseFloat(totalData.totalCost);

    const solarProject = parseFloat(
      solarData.breakdown.find(i => i.name === "Solar-Project")?.cost || 0
    );

    const windProject = parseFloat(
      windData.breakdown.find(i => i.name === "Wind-Project")?.cost || 0
    );

    const noTag = (total - solarProject - windProject) / 2;

    return {
      noTag: noTag.toFixed(2)
    };
  };

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setView("services");
    window.history.pushState({ view: "services", accountId: account.id }, "");

    // loadAccountTrend doesn't depend on dateRange (always covers the
    // fiscal year to date), so it's always safe to fetch.
    loadAccountTrend(account);

    // dateRange can still hold an invalid future range here — the Accounts
    // list skips fetching for that (see handleFilterChange) but still saves
    // the range, so a stale accounts table can stay on screen with a bad
    // date selected. Don't fire a doomed fetch for it; the same warning
    // banner already renders on this view so the reason is clear. Also
    // clear out whichever account's service data is still in memory from
    // before — otherwise it would render here under the newly-selected
    // account's name, misattributing one account's costs to another.
    if (dateRangeWarning) {
      setTotalData(null);
      setSolarData(null);
      setWindData(null);
      setServiceData(null);
      return;
    }

    loadAccountServices(account, dateRange);
  };

  const goToAccountsView = () => {
    if (view === "services") {
      window.history.back();
    }
  };

  // The only thing that ever changes `view` in response to Back/Forward —
  // both the real browser buttons and the in-app back affordances (which
  // just call window.history.back()) funnel through this one popstate
  // handler, so they always stay consistent. Doesn't clear
  // selectedAccount/serviceData/etc on the way back to "accounts" —
  // keeping them in memory means pressing Forward restores the same view
  // instantly with no refetch; a fresh handleSelectAccount click still
  // always overwrites them.
  useEffect(() => {
    window.history.replaceState({ view: "accounts" }, "");

    const onPopState = (event) => {
      setView(event.state?.view === "services" ? "services" : "accounts");
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const computed = calculateAdjustedCosts(totalData, solarData, windData);

  const Card = ({ title, value, accent }) => (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex-1 min-w-[180px] border-t-4 ${accent}`}>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
      <p className="text-2xl font-bold text-slate-900 mt-2">{formatCurrency(value || 0)}</p>
    </div>
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-400 text-sm">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={setUser} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <button
            type="button"
            onClick={goToAccountsView}
            className="flex items-center gap-4 hover:opacity-80 transition-opacity"
          >
            <img src={soriginLogo} alt="Sorigin" className="h-8 w-auto" />
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="text-xl font-bold leading-tight text-brand-dark">AWS Cost Dashboard</h1>
          </button>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="hidden sm:inline">Reporting period</span>
              <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">
                {dayjs(dateRange.startDate).format("MMM D, YYYY")} – {dayjs(dateRange.endDate).format("MMM D, YYYY")}
              </span>
            </div>

            <div className="h-6 w-px bg-slate-200 hidden sm:block" />

            <button
              type="button"
              onClick={handleLogout}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 w-full flex-1">
        {view === "accounts" && (
          <>
            <div className="flex gap-4 mb-6 flex-wrap">
              <TrendKpiCard
                title="Year to Date Spend"
                value={ytdTotal}
                accent="border-t-brand-dark"
                sparklineData={yearlySparkline}
                sparklineColor="#076333"
                sparklineId="sparkline-ytd"
                onExpand={() => setExpandedKpi("yearly")}
              />
              <TrendKpiCard
                title={currentQuarter ? `Quarterly Spend – ${currentQuarter.label}` : "Quarterly Spend"}
                value={currentQuarterTotal}
                accent="border-t-violet-400"
                sparklineData={currentQuarterSparkline}
                sparklineColor="#4a3aa7"
                sparklineId="sparkline-quarter"
                onExpand={() => setExpandedKpi("quarterly")}
              />
            </div>

            <DateFilter
              preset={preset}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleFilterChange}
            />

            {dateRangeWarning && (
              <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
                {dateRangeWarning}
              </div>
            )}

            <div className="flex gap-4 mb-6 flex-wrap">
              <TrendKpiCard
                title="Development Spend"
                value={devSpend}
                accent="border-t-amber-400"
                onExpand={() => setExpandedKpi("dev")}
              />
              <TrendKpiCard
                title="Production Spend"
                value={prodSpend}
                accent="border-t-sky-400"
                onExpand={() => setExpandedKpi("prod")}
              />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
                Accounts
              </h3>
              <AccountOrgTree
                data={accountsData}
                loading={accountsLoading}
                onSelectAccount={handleSelectAccount}
              />
            </div>
          </>
        )}

        {view === "services" && selectedAccount && (
          <>
            <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap">
              <button
                onClick={goToAccountsView}
                className="flex items-center gap-1 text-brand-dark hover:text-brand font-medium"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                All Accounts
              </button>
              <span className="text-slate-300">/</span>
              <span className="text-slate-700 font-medium">{selectedAccount.name}</span>
              <span className="text-slate-400 font-mono text-xs">({selectedAccount.id})</span>
            </nav>

            <DateFilter
              preset={preset}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleFilterChange}
            />

            {dateRangeWarning && (
              <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
                {dateRangeWarning}
              </div>
            )}

            {selectedAccount.id === SORIGIN_PROD_ACCOUNT_ID ? (
              <>
                <div className="flex gap-4 mb-6 flex-wrap">
                  <Card title="Total Cost" value={totalData?.totalCost} accent="border-t-brand-dark" />
                  <Card
                    title="Solar Cost"
                    accent="border-t-amber-400"
                    value={
                      totalData && solarData && windData
                        ? (
                            parseFloat(computed.noTag) +
                            parseFloat(
                              solarData.breakdown.find(i => i.name === "Solar-Project")?.cost || 0
                            )
                          ).toFixed(2)
                        : 0
                    }
                  />
                  <Card
                    title="Wind Cost"
                    accent="border-t-sky-400"
                    value={
                      totalData && solarData && windData
                        ? (
                            parseFloat(computed.noTag) +
                            parseFloat(
                              windData.breakdown.find(i => i.name === "Wind-Project")?.cost || 0
                            )
                          ).toFixed(2)
                        : 0
                    }
                  />
                </div>

                <AccountServicesPanel
                  title="Service Wise Cost Breakdown"
                  costTableData={totalData}
                  costTableLoading={servicesLoading}
                  trendData={accountTrendBuckets}
                  trendServices={topServices.services}
                  trendHasOther={topServices.hasOther}
                  trendOtherColor={topServices.otherColor}
                  trendLoading={accountTrendLoading}
                  granularity={accountTrendGranularity}
                  onGranularityChange={setAccountTrendGranularity}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
                      Solar Cost Breakdown
                    </h3>
                    <CostTable
                      data={solarData}
                      loading={servicesLoading}
                      overrideNoTag={computed.noTag}
                    />
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
                      Wind Cost Breakdown
                    </h3>
                    <CostTable
                      data={windData}
                      loading={servicesLoading}
                      overrideNoTag={computed.noTag}
                    />
                  </div>
                </div>
              </>
            ) : (
              <AccountServicesPanel
                title="Service Wise Cost Breakdown"
                costTableData={serviceData}
                costTableLoading={servicesLoading}
                trendData={accountTrendBuckets}
                trendServices={topServices.services}
                trendHasOther={topServices.hasOther}
                trendOtherColor={topServices.otherColor}
                trendLoading={accountTrendLoading}
                granularity={accountTrendGranularity}
                onGranularityChange={setAccountTrendGranularity}
              />
            )}
          </>
        )}
      </main>

      <footer className="max-w-7xl mx-auto px-6 py-6 text-xs text-slate-400 w-full">
        Data sourced from AWS Cost Explorer · Figures in USD · Sorigin Cloud FinOps
      </footer>

      {expandedKpi === "yearly" && (
        <Modal title="Year to Date Spend — Account Breakdown" onClose={() => setExpandedKpi(null)}>
          <div className="flex items-center gap-2 mb-4">
            {[
              { value: "daily", label: "Daily" },
              { value: "weekly", label: "Weekly" },
              { value: "monthly", label: "Monthly" }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setYearlyGranularity(option.value)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  yearlyGranularity === option.value
                    ? "bg-brand-dark text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {trendLoading ? (
            <p className="text-slate-400 text-sm py-10 text-center">Loading trend data…</p>
          ) : (
            <AccountTrendChart
              data={yearlyAccountBuckets}
              services={topAccounts.accounts}
              hasOther={topAccounts.hasOther}
              otherColor={topAccounts.otherColor}
              groupTooltip={user.role !== "team_member"}
              height={320}
            />
          )}

          <p className="text-xs text-slate-400 mt-3">
            {trendDaily[0] ? dayjs(trendDaily[0].date).format("MMM D, YYYY") : "FY start"}
            {trendAsOf ? ` – ${dayjs(trendAsOf).format("MMM D, YYYY")}` : ""} · hover any point for the exact
            {yearlyGranularity === "daily" ? " daily" : yearlyGranularity === "weekly" ? " weekly" : " monthly"} spend, per account.
          </p>
        </Modal>
      )}

      {expandedKpi === "quarterly" && (
        <Modal title="Financial Quarter Spend (FY starts April)" onClose={() => setExpandedKpi(null)}>
          <div className="flex items-center gap-2 mb-5">
            {[
              { value: "daily", label: "Daily" },
              { value: "weekly", label: "Weekly" },
              { value: "monthly", label: "Monthly" }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setQuarterlyGranularity(option.value)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  quarterlyGranularity === option.value
                    ? "bg-brand-dark text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {trendLoading ? (
            <p className="text-slate-400 text-sm py-10 text-center">Loading trend data…</p>
          ) : (
            <div className="space-y-8">
              {quarterTotals.map(q => (
                <div key={q.quarterNumber}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h4 className="text-sm font-semibold text-slate-700">
                      {q.label}
                      {q.isCurrent && (
                        <span className="ml-2 text-xs font-medium text-brand-dark bg-brand-50 px-2 py-0.5 rounded-full">
                          In progress
                        </span>
                      )}
                    </h4>
                    <span className="text-sm font-bold text-slate-900">{formatCurrency(q.total)}</span>
                  </div>
                  <AccountTrendChart
                    data={
                      quarterlyGranularity === "daily"
                        ? bucketAllAccountsTrendByDayInRange(trendDaily, q.startDate, q.endDate, topAccounts.accounts)
                        : quarterlyGranularity === "weekly"
                        ? bucketAllAccountsTrendByWeekInRange(trendDaily, q.startDate, q.endDate, topAccounts.accounts, trendAsOf)
                        : bucketAllAccountsTrendByQuarterMonths(trendDaily, q.monthsInQuarter, fiscalYear, topAccounts.accounts, trendAsOf)
                    }
                    services={topAccounts.accounts}
                    hasOther={topAccounts.hasOther}
                    otherColor={topAccounts.otherColor}
                    groupTooltip={user.role !== "team_member"}
                    height={220}
                  />
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-slate-400 mt-3">
            Periods that haven't happened yet are shown on the axis with no bar/line so you can see where you are in the quarter.
          </p>
        </Modal>
      )}

      {expandedKpi === "dev" && (
        <Modal title="Development Spend Breakdown" onClose={() => setExpandedKpi(null)}>
          <p className="text-2xl font-bold text-slate-900 mb-1">{formatCurrency(devSpend)}</p>
          <p className="text-xs text-slate-400 mb-5">
            Accounts tagged "dev" · {dayjs(dateRange.startDate).format("MMM D, YYYY")} – {dayjs(dateRange.endDate).format("MMM D, YYYY")}
          </p>
          <AccountBarBreakdown data={devAccounts} color="#eda100" />
        </Modal>
      )}

      {expandedKpi === "prod" && (
        <Modal title="Production Spend Breakdown" onClose={() => setExpandedKpi(null)}>
          <p className="text-2xl font-bold text-slate-900 mb-1">{formatCurrency(prodSpend)}</p>
          <p className="text-xs text-slate-400 mb-5">
            Accounts tagged "prod" · {dayjs(dateRange.startDate).format("MMM D, YYYY")} – {dayjs(dateRange.endDate).format("MMM D, YYYY")}
          </p>
          <AccountBarBreakdown data={prodAccounts} color="#38bdf8" />
        </Modal>
      )}
    </div>
  );
}

export default App;
